import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

interface Props {
  existingNames: string[];
  onAdd: (name: string) => void;
}

const normalize = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  return trimmed.endsWith("_TagMap") ? trimmed : `${trimmed}_TagMap`;
};

const AddTagMapButton: React.FC<Props> = ({ existingNames, onAdd }) => {
  const theme = useTheme();
  const accent = theme.palette.status.accent;
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const normalized = normalize(name);
  const collides = normalized && existingNames.includes(normalized);
  const helper = collides
    ? `${normalized} already exists`
    : normalized
      ? `Will be saved as ${normalized}`
      : "Suffix _TagMap is added automatically.";

  const submit = () => {
    if (!normalized || collides) return;
    onAdd(normalized);
    setName("");
    setOpen(false);
  };

  if (!open) {
    return (
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => setOpen(true)}
        sx={{ textTransform: "none", color: accent }}
      >
        Add tag map
      </Button>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          size="small"
          autoFocus
          label="Tag map name"
          placeholder="e.g. interior"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") {
              setName("");
              setOpen(false);
            }
          }}
          error={Boolean(collides)}
          sx={{ minWidth: 240 }}
        />
        <Button
          size="small"
          variant="contained"
          onClick={submit}
          disabled={!normalized || Boolean(collides)}
          sx={{
            textTransform: "none",
            backgroundColor: accent,
            "&:hover": { backgroundColor: accent },
          }}
        >
          Add
        </Button>
        <Button
          size="small"
          onClick={() => {
            setName("");
            setOpen(false);
          }}
          sx={{ textTransform: "none", color: "text.secondary" }}
        >
          Cancel
        </Button>
      </Stack>
      <Typography
        variant="caption"
        sx={{
          mt: 0.5,
          display: "block",
          color: collides ? "error.main" : "text.secondary",
        }}
      >
        {helper}
      </Typography>
    </Box>
  );
};

export default AddTagMapButton;
